// @givmo/app-admin — employer BULK gift-card compose panel (EP-WEB5).
//
// The bulk analogue of the single-send `ComposePanel`: pick a RECIPIENT SET (roster multi-select +
// CSV/text paste), one UNIFORM denomination + one shared optional message for the whole batch (D-EP-16),
// then a funding source, then Review → the money-moving batch confirm dialog. Presentational: the PAGE
// owns the compose state, the page-owned idempotency holder, and the confirm dialog (which survives a
// close/reopen), exactly like the single-send flow.
//
// Wallet sufficiency is checked against the BATCH TOTAL (denomination × recipient count), not a single
// card. Members cannot deposit, so an over-total wallet points to an Owner rather than offering a
// deposit action.

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Select,
  Skeleton,
  Textarea,
  TextField,
  testIdProp,
  type SelectOption,
} from '@givmo/design-system';
import { Wallet as WalletIcon } from 'lucide-react';
import {
  formatGiftCardDollars,
  GIFT_CARD_PRESETS,
  GIFT_CARD_RANGE_LABEL,
} from './gift-cards-decode';
import { formatBatchFaceTotal, type BatchRecipient } from './gift-cards-batch-decode';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { BulkRecipientPicker } from './bulk-recipient-picker';

const MESSAGE_MAX = 500;
const NAME_MAX = 200;

export interface BulkComposePanelProps {
  recipients: BatchRecipient[];
  onRecipientsChange: (next: BatchRecipient[]) => void;
  maxCount: number;

  denominationRaw: string;
  onDenominationChange: (v: string) => void;
  denomination: number | null;
  denominationError: boolean;

  batchName: string;
  onBatchNameChange: (v: string) => void;
  recipientMessage: string;
  onRecipientMessageChange: (v: string) => void;

  fundingValue: string | null;
  onFundingChange: (v: string | null) => void;
  fundingOptions: SelectOption[];
  fundingLoading: boolean;
  walletDisplay: string;
  walletInsufficient: boolean;

  canReview: boolean;
  onReview: () => void;
  onCancel: () => void;
}

export function BulkComposePanel({
  recipients,
  onRecipientsChange,
  maxCount,
  denominationRaw,
  onDenominationChange,
  denomination,
  denominationError,
  batchName,
  onBatchNameChange,
  recipientMessage,
  onRecipientMessageChange,
  fundingValue,
  onFundingChange,
  fundingOptions,
  fundingLoading,
  walletDisplay,
  walletInsufficient,
  canReview,
  onReview,
  onCancel,
}: BulkComposePanelProps) {
  // Who adds funds, and where, is this host's (EP-MNT5(h)).
  const { words } = useEmployerModulePaths();
  const count = recipients.length;
  const faceTotalLabel =
    denomination !== null && count > 0 ? formatBatchFaceTotal(denomination, count) : null;

  return (
    <div className="flex flex-col gap-24" {...testIdProp('gift-card-bulk-compose')}>
      {/* ── Step 1: recipients ── */}
      <Card>
        <CardHeader
          title="Who is it for?"
          subtitle="Pick employees from your roster, or paste a list of work emails."
        />
        <CardBody>
          <BulkRecipientPicker
            selected={recipients}
            onChange={onRecipientsChange}
            maxCount={maxCount}
          />
        </CardBody>
      </Card>

      {/* ── Step 2: uniform denomination + optional batch name / shared message ── */}
      <Card>
        <CardHeader
          title="How much each?"
          subtitle={`Every recipient gets the same amount. Whole dollars, ${GIFT_CARD_RANGE_LABEL}. No fees are deducted — each gift is worth its full face value.`}
        />
        <CardBody className="flex flex-col gap-16">
          <div className="flex flex-wrap gap-8" {...testIdProp('gift-card-bulk-presets')}>
            {GIFT_CARD_PRESETS.map((preset) => {
              const active = denomination === preset;
              return (
                <Button
                  key={preset}
                  appearance="saas"
                  tone={active ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onDenominationChange(String(preset))}
                  testId={`gift-card-bulk-preset-${preset}`}
                >
                  {formatGiftCardDollars(preset)}
                </Button>
              );
            })}
          </div>

          <TextField
            label="Amount per recipient (USD)"
            inputMode="numeric"
            placeholder="0"
            value={denominationRaw}
            onChange={(e) => onDenominationChange(e.target.value)}
            error={denominationError}
            errorMessage={
              denominationError
                ? `Enter a whole-dollar amount from ${GIFT_CARD_RANGE_LABEL}.`
                : undefined
            }
            className="max-w-[220px]"
            testId="gift-card-bulk-denomination-input"
          />

          <TextField
            label="Batch name (optional)"
            placeholder="e.g. Q3 team bonus"
            value={batchName}
            maxLength={NAME_MAX}
            onChange={(e) => onBatchNameChange(e.target.value)}
            className="max-w-[360px]"
            testId="gift-card-bulk-name"
          />

          <Textarea
            label="Message to all recipients (optional)"
            placeholder="Add a personal note sent with every gift…"
            value={recipientMessage}
            maxLength={MESSAGE_MAX}
            onChange={(e) => onRecipientMessageChange(e.target.value)}
            helper={`${recipientMessage.length}/${MESSAGE_MAX}`}
            className="max-w-[480px]"
            testId="gift-card-bulk-message"
          />

          {faceTotalLabel !== null && (
            <p
              className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
              {...testIdProp('gift-card-bulk-face-total')}
            >
              {count} × {formatGiftCardDollars(denomination as number)} ={' '}
              <span className="font-w700 text-text-strong">{faceTotalLabel}</span> total face value
            </p>
          )}
        </CardBody>
      </Card>

      {/* ── Step 3: funding ── */}
      <Card>
        <CardHeader
          title="Pay with"
          subtitle="One charge (or one wallet debit) funds the whole batch."
        />
        <CardBody className="flex flex-col gap-12">
          {fundingLoading ? (
            <Skeleton className="h-[40px] w-full max-w-[360px] rounded-lg" />
          ) : (
            <Select
              label="Funding source"
              options={fundingOptions}
              value={fundingValue}
              onValueChange={onFundingChange}
              className="max-w-[360px]"
              testId="gift-card-bulk-payment-select"
            />
          )}

          {walletInsufficient && (
            <div
              className="flex items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12"
              {...testIdProp('gift-card-bulk-wallet-insufficient')}
            >
              <Icon icon={WalletIcon} size={16} aria-hidden className="mt-1 text-danger-700" />
              <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                Insufficient wallet balance ({walletDisplay}) for this batch total —{' '}
                {words.giftCardAddFunds}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Review → the money-moving batch confirm dialog ── */}
      <div className="flex items-center justify-end gap-12">
        <Button
          appearance="saas"
          tone="ghost"
          size="lg"
          onClick={onCancel}
          testId="gift-card-bulk-compose-cancel"
        >
          Cancel
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          disabled={!canReview}
          onClick={onReview}
          testId="gift-card-bulk-review-submit"
        >
          {faceTotalLabel !== null ? `Review — send ${faceTotalLabel}` : 'Review batch'}
        </Button>
      </div>
    </div>
  );
}
